import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { RootState } from 'reducers';
import { Constants, Globals } from 'utils';
import { HomeService } from 'services';
import { ContactCallIcon, ContactMessageIcon } from 'assets/icons';
import { DatePicker } from 'components';
import PaymentMethodsModal from 'screens/affiliate/components/payment-methods-modal';
import AdPaymentModal from 'screens/affiliate/components/ad-payment-modal';
import DisclaimerModal from 'screens/affiliate/components/disclaimer-modal';
import AddClassifiedModal from './add-classified-modal';

const MODAL = {
  NONE: 0,
  CLASSIFIED: 1,
  DISCLAIMER: 2,
  PAYMENT_METHODS: 3,
  PAYMENT: 4,
};

const Classified = () => {
  const { canResetFilters, data, filters, form, modal, onChange, onChangeFilters, onFillForm, onReset, onResetFilters, selects, setModal } = useForm();

  return (
    <main id="classified">
      <AddClassifiedModal
        visible={modal === MODAL.CLASSIFIED}
        onSubmit={data => {
          onFillForm(data);
          setModal(MODAL.DISCLAIMER);
        }}
        onClose={() => {
          setModal(MODAL.NONE);
          onReset();
        }}
      />

      <DisclaimerModal
        visible={modal === MODAL.DISCLAIMER}
        onClose={() => setModal(MODAL.PAYMENT_METHODS)}
      />

      <PaymentMethodsModal
        visible={modal === MODAL.PAYMENT_METHODS}
        onClose={methodId => {
          if (methodId) {
            onChange(methodId, 'payment_method_id');
            setTimeout(() => setModal(MODAL.PAYMENT), 100);
          } else {
            setModal(MODAL.NONE);
          }
        }}
        paymentMethods={selects.paymentMethods}
      />
      {modal === MODAL.PAYMENT && (
        <AdPaymentModal
          visible
          type="classified"
          paymentData={form}
          onClose={() => setModal(MODAL.NONE)}
          onSuccess={onReset}
        />
      )}

      <div className="filters">
        <DatePicker
          placeholder="Desde"
          value={filters.since}
          onChange={(v: string) => onChangeFilters(v, 'since')}
          showYearDropdown

        />
        <DatePicker
          placeholder="Hasta"
          value={filters.until}
          onChange={(v: string) => onChangeFilters(v, 'until')}
          showYearDropdown

        />

        {canResetFilters && (
          <button onClick={onResetFilters}>Limpiar</button>
        )}
      </div>

      <section className="classified-registry">
        <div className="classified-registry__header">
          <h2 className="classified-registry__title">Registro de clasificados</h2>
          <button className="classified-registry__add" onClick={() => setModal(MODAL.CLASSIFIED)}>
            PUBLICAR CLASIFICADO
          </button>
        </div>

        <div className="classified-list">
          {data.map((hist: any, index: number) => {
            const detail = hist.details && hist.details.length > 0 ? hist.details[0] : null;
            const histTitle = detail?.title || 'Clasificado sin título';
            const histContent = detail?.content || 'Sin descripción.';

            let contactMethods: string[] = [];
            if (hist.user?.consultation?.contact_methods) {
              try {
                const raw = hist.user.consultation.contact_methods;
                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                if (Array.isArray(parsed)) {
                  contactMethods = parsed.map(String);
                }
              } catch (e) {
                console.warn('Error parseando contact_methods', e);
              }
            }

            const hasCall = contactMethods.includes('2');
            const hasChat = contactMethods.includes('3');

            return (
              <div key={index} className="classified-card">
                <p className="title">{histTitle}</p>
                <p className="content">{histContent}</p>

                <div className="footer">
                  <p>{moment(hist.created_at).format('DD-MM-YYYY')}</p>
                  <div className="contact-methods">
                    {hasChat && (
                      <img className="contact-icon" title="Mensajes" src={ContactMessageIcon} alt="Chat" />
                    )}
                    {hasCall && (
                      <img className="contact-icon" title="Llamadas" src={ContactCallIcon} alt="Llamada" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {(!data.length) && (
            <p className="empty">No hay registros</p>
          )}
        </div>
      </section>
    </main>
  )
}

const useForm = () => {
  const INITIAL_FORM = {
    advertising_rate_id: '',
    payment_method_id: '',
    days: '',
    image: '',
    image_blob: '',
    amount: 0,
    seller_code: '',
  }

  const INITIAL_FILTERS = {
    type: 1,
    since: '',
    until: '',
  };

  const user = useSelector((state: RootState) => state.user);
  const [modal, setModal] = useState(MODAL.NONE);
  const [data, setData] = useState<any[]>([]);
  const [selects, setSelects] = useState({
    paymentMethods: [],
  });
  const [form, setForm] = useState(INITIAL_FORM);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const getSelects = async () => {
    try {
      const selectsRes: any = await HomeService.affiliate.getSelects({ user_id: user?.id });
      selectsRes.paymentMethods = selectsRes.paymentMethods.filter((i: any) => i.id !== Constants.PAYMENT_METHODS.CASH);
      setSelects(selectsRes || {});

      // const catResponse: any = await HomeService.affiliate.getBanners();
      // if (catResponse?.banners) {
      //   setAvailableBanners(catResponse.banners);
      // }

    } catch (error) {
      console.error("Error cargando data de banners", error);
    }
  }

  const getData = async () => {
    try {
      const _filters = await Globals.getUpdatedState(setFilters);
      const payload = {
        ..._filters,
        user_id: user?.id,
      }
      const histResponse: any = await HomeService.affiliate.getAdvertisements(payload);
      if (histResponse?.advertisements?.rows) {
        setData(histResponse.advertisements.rows);
      } else if (histResponse?.data) {
        setData(histResponse.data);
      } else if (Array.isArray(histResponse)) {
        setData(histResponse);
      } else {
        setData([]);
      }

    } catch (error) {
      console.log(error);
    }
  }

  const onChange = <T extends keyof typeof form>(value: any, target: T) => {
    setForm(s => ({ ...s, [target]: value }));
  }

  const onFillForm = (data: any) => {
    setForm(s => ({ ...s, ...data }));
  }

  const onChangeFilters = <T extends keyof typeof filters>(value: any, target: T) => {
    setFilters(s => ({ ...s, [target]: value }));
    getData();
  }

  const onReset = () => {
    setForm(INITIAL_FORM);
    setModal(MODAL.NONE);
    getData();
  }

  const onResetFilters = () => {
    setFilters(INITIAL_FILTERS);
    getData();
  }

  useEffect(() => {
    getData();
    getSelects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canResetFilters = useMemo(() => {
    return JSON.stringify(filters) !== JSON.stringify(INITIAL_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return {
    canResetFilters,
    data,
    filters,
    form,
    modal,
    onChange,
    onChangeFilters,
    onFillForm,
    onReset,
    onResetFilters,
    selects,
    setModal,
  }
}

export default Classified